import './index.scss'
import { Empty } from 'antd'
import { useCallback, useEffect, useState, useMemo } from "react"
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { homeModels, userActivityReport } from '@/api'
import ModelItem from '@/components/ModelItem'
import ModelAdCard from '@/components/Advertisement/ModelAdCard'
import SiteTip from '@/views/components/SiteTip'
import ScrollLoad from '@/components/ScrollLoad'
import { useDebounceFn } from 'ahooks'
import { insertAdsIntoList, isAdItem } from '@/utils/adInserter'
import { adConfig, getRandomAd } from '@/config/ads.config'
import { tosConfig } from '@/config'

// 处理模型图片URL，包含modelCover的拼接为直连地址
const processModelImgUrl = (url) => {
  if (!url) return url
  if (url.includes('avatar') || url.includes('cover') || url.includes('modelCover') || url.includes('materialCover')) {
    // 如果已经是完整URL则不处理
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    return `${tosConfig.baseUrl}${url}`
  }
  return url
}

const Home = () => {

  const navigate = useNavigate()
  const [pager, setPager] = useState({
    page: 1,
    limit: 20,
    total: 0
  })
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(false)
  const user = useSelector((state) => state.user.userInfo)

  const fetchList = async () => {
    let oldList = [...list]
    if (pager.page === 1) {
      oldList = []
    }
    oldList = oldList.concat(Array.from({ length: 12 }).fill({ loading: true }))
    setList(oldList)
    setLoading(true)
    try {
      const res = await homeModels({
        page: pager.page,
        limit: pager.limit
      })
      if (res.code === 0 && res.data.list && res.data.list.length > 0) {
        // 处理模型图片URL
        const processedList = res.data.list.map(item => ({
          ...item,
          modelImgUrl: processModelImgUrl(item.modelImgUrl)
        }))
        oldList.splice(oldList.length - 12, 12, ...processedList)
        const param = { ...pager }
        param.total = res.data.total
        setPager(param)
        setList([...oldList])
      } else {
        const param = { ...pager }
        param.total = 0
        setPager(param)
        setList([])
      }
      setLoading(false)
    } catch (e) {
      setList([])
      setLoading(false)
    }
  }

  // 插入广告后的列表
  const listWithAds = useMemo(() => {
    if (!adConfig?.homeCards?.enabled) {
      return list
    }
    if (!list || list.length === 0) {
      return list
    }
    // 过滤掉加载中的占位符和已有的广告项
    const realModels = list.filter(item => !item.loading && !item.isAd)
    if (realModels.length === 0) {
      return list
    }
    // 插入广告
    const modelsWithAds = insertAdsIntoList(
      realModels,
      adConfig.homeCards,
      () => getRandomAd('homeCard')
    )
    // 如果有加载中的占位符，添加到末尾（不影响广告位置）
    const loadingItems = list.filter(item => item.loading)
    return [...modelsWithAds, ...loadingItems]
  }, [list])

  const handleReachBottom = useCallback(() => {
    setPager(prev => ({
      ...prev,
      page: prev.page + 1
    }))
  }, [])

  const handleJump = () => {
    navigate('/category')
  }

  const { run } = useDebounceFn(
    async () => {
      let res = null
      try {
        res = await fetch('https://api.ipify.org?format=json').then(response => response.json())
      } catch (e) {
        console.log(e)
      }
      userActivityReport({
        userId: (user && user.id) ? user.id : null,
        userIp: res ? res.ip : null,
        pageUrl: window.location.pathname,
        activityDate: new Date().toISOString().split('T')[0],
        country: res ? res.country : '',
        province: res ? res.province : '',
        city: res ? res.city : '',
        area: res ? res.area : ''
      })
    },
    {
      wait: 1000
    }
  )

  useEffect(() => {
    run()
  }, [])

  useEffect(() => {
    fetchList()
  }, [pager.page, pager.limit])

  const loadFinish = pager.page >= Math.ceil((pager.total / pager.limit))

  return (
    <>
      <ScrollLoad
        loading={ loading }
        loadFinish={ loadFinish }
        onReachBottom={ handleReachBottom }
      >
        <div className="home">
          <div className="banner">
            <div className="gd_text">
              <div className="trans_color">
                <div className="marquee-content">
                  <span>Over 10 million ready to work 3D models!  超过1000万个优质3D模型免费下载！</span>
                  <span>Over 10 million ready to work 3D models!  超过1000万个优质3D模型免费下载！</span>
                </div>
              </div>
            </div>
            <h2>Where Dreams Come True!</h2>
            <div className="start" onClick={ handleJump }>开始探索</div>
          </div>
          <div className="model-container">
            <div className="model-grid">
              {
                listWithAds && listWithAds.length > 0 && listWithAds.map((item, index) => {
                  // 判断是否为广告项
                  if (isAdItem(item)) {
                    return (
                      <div key={ item.id || `ad_${index}` } className="model-grid-item">
                        <ModelAdCard adData={item.adData} />
                      </div>
                    )
                  }
                  // 模型项
                  return (
                    <div key={ `model_${index}` } className="model-grid-item">
                      <ModelItem
                        key={ `model_${index}` }
                        item={ item }
                      />
                    </div>
                  )
                })
              }
              {
                !loading
                  && <>
                      {
                        !listWithAds || listWithAds.length === 0
                          ? <div style={{ padding: '80px 20px', textAlign: 'center', gridColumn: '1 / -1' }}>
                              <Empty
                                description={
                                  <span style={{
                                    color: '#86868b',
                                    fontSize: '17px',
                                    fontWeight: '400'
                                  }}>
                                    暂无模型数据
                                  </span>
                                }
                              />
                            </div>
                          : null
                      }
                      {
                        pager.page < Math.ceil(pager.total / pager.limit)
                          ? null
                          : listWithAds && listWithAds.length > 0 && (
                              <div style={{ marginTop: '60px', marginBottom: '40px', textAlign: 'center', gridColumn: '1 / -1' }}>
                                <div style={{
                                  color: '#86868b',
                                  fontSize: '15px',
                                  fontWeight: '400',
                                  letterSpacing: '0.2px'
                                }}>
                                  已显示全部内容
                                </div>
                              </div>
                            )
                      }
                    </>
              }
            </div>
          </div>
          <SiteTip />
        </div>
      </ScrollLoad>
    </>
  )
}

export default Home
